import { StateCreator } from "zustand";
import { toast } from "react-toastify";
import { CommonResponseModel } from "../../../Models/tableDetailsModel";
import agentsApi from "../agents.api";
import { buildAgentsPayload } from "../agents.utils";
import { IGetAllAgentsResp, IUserType } from "../agents.model";
import { IState } from "../agentDetail.model";
import { AgentsAsyncActions, AgentsStore } from "./agentsTypes";
import { getApiErrorMessage } from "../../../utils/getApiErrorMessage";
import { shouldShowErrorToast } from "../../../utils/apiRequestError";

export const createAgentsAsyncActions: StateCreator<AgentsStore, [], [], AgentsAsyncActions> = (set, get) => ({
    fetchAllAgents: async (opts) => {
        const { setShowLoader, force = false } = opts ?? {};
        const {
            isAgentsLoading,
            pageIndex,
            pageSize,
            searchParam,
            filters,
            sortRequest,
            hasLoadedAgents,
        } = get();

        if (isAgentsLoading && !force) return;
        if (hasLoadedAgents && !force) return;

        try {
            set({ isAgentsLoading: true, agentsError: null });
            setShowLoader?.(true);

            const payload = buildAgentsPayload({
                pageIndex,
                pageSize,
                filters,
                searchParam,
                sort: sortRequest,
            });

            const response: CommonResponseModel<IGetAllAgentsResp> = 
                await agentsApi.GetAllAgentsDetails.post(payload);

            if (response.flagStatus && response.value) {
                set({
                    agentsDetails: response.value.agentsDetails,
                    agentsCountWithSegregation: response.value.agentsCountWithSegregation,
                    agentPermissions: response.value.agentPermissions,
                    availableFilters: response.value.availableFilters ?? [],
                    hasLoadedAgents: true,
                });
            } else {
                toast.error(response.error ?? "Failed to fetch agents");
            }
        } catch (error) {
            const message = getApiErrorMessage(error, "Failed to fetch agents");
            set({ agentsError: message, agentsDetails: [], hasLoadedAgents: false });
            if (shouldShowErrorToast(error)) {
                toast.error(message);
            }
        } finally {
            set({ isAgentsLoading: false });
            setShowLoader?.(false);
        }
    },

    fetchAllStates: async () => {
        const { allStates, allStatesLoading } = get();
        if (allStates !== null || allStatesLoading) return;

        try {
            set({ allStatesLoading: true });
            const response: CommonResponseModel<IState[]> = 
                await agentsApi.GetAllStates.get();
            if (response.flagStatus) {
                set({ allStates: response.value ?? [] });
            }
        } catch (error) {
            console.error("Failed to fetch states:", error);
        } finally {
            set({ allStatesLoading: false });
        }
    },

    fetchFilterLists: async () => {
        const { filterListsLoading, agenciesList, agentsList, userTypesList } = get();
        if (filterListsLoading) return;
        if (agenciesList !== null && agentsList !== null && userTypesList !== null) return;

        try {
            set({ filterListsLoading: true });
            const [agenciesResponse, agentsResponse, userTypesResponse] = await Promise.all([
                agentsApi.GetAgenciesList.get(),
                agentsApi.GetAgentsList.get(),
                agentsApi.GetAllUserTypes.get(),
            ]);

            const agenciesRes = agenciesResponse as CommonResponseModel<string[]>;
            const agentsRes = agentsResponse as CommonResponseModel<string[]>;
            const userTypesRes = userTypesResponse as CommonResponseModel<IUserType[]>;

            set({
                agenciesList: agenciesRes.flagStatus ? agenciesRes.value ?? [] : [],
                agentsList: agentsRes.flagStatus ? agentsRes.value ?? [] : [],
                userTypesList: userTypesRes.flagStatus ? userTypesRes.value ?? [] : [],
            });
        } catch (error) {
            console.error("Failed to fetch filter lists:", error);
            if (shouldShowErrorToast(error)) {
                toast.error(getApiErrorMessage(error, "Failed to fetch filter options"));
            }
        } finally {
            set({ filterListsLoading: false });
        }
    },

    resendInviteToAgent: async (email: string, opts?: { setShowLoader?: React.Dispatch<React.SetStateAction<boolean>> }) => {
        const { setShowLoader } = opts ?? {};
        if (!email) return;
        
        try {
            setShowLoader?.(true);
            const response: CommonResponseModel<string> = await agentsApi.ResendInviteToAgent.post(email);

            if (response.flagStatus) {
                set({ hasLoadedAgents: false });
                toast.success(response.value ?? "Invite resent successfully");
            } else {
                toast.error(response.error ?? "Failed to resend invite");
            }
        } catch (error) {
            if (shouldShowErrorToast(error)) {
                toast.error(getApiErrorMessage(error, "Failed to resend invite"));
            }
        } finally {
            setShowLoader?.(false);
        }
    },
});
